import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  IconButton,
  Button,
  Chip,
  Pagination,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Divider,
  Stack,
} from '@mui/material';
import {
  PersonAdd,
  Event,
  Inventory,
  Payment,
  AttachMoney,
  Description,
  Warning,
  Info,
  Delete,
  MoreVert,
  ShoppingCart,
  FilterList,
} from '@mui/icons-material';
import api from '../services/api';
import { useNotifications } from '../context/NotificationContext';
import { formatDistanceToNow } from 'date-fns';

const Notifications = () => {
  const navigate = useNavigate();
  const { fetchUnreadCount } = useNotifications();

  const [activeTab, setActiveTab] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [typeFilter, setTypeFilter] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [error, setError] = useState(null);

  const perPage = 20;

  const notificationTypes = [
    { value: 'lead_new', label: 'New Lead' },
    { value: 'lead_status', label: 'Lead Status' },
    { value: 'sale_scheduled', label: 'Sale Scheduled' },
    { value: 'sale_completed', label: 'Sale Completed' },
    { value: 'appointment_reminder', label: 'Appointment Reminder' },
    { value: 'inventory_low', label: 'Low Inventory' },
    { value: 'payment_received', label: 'Payment Received' },
    { value: 'commission_ready', label: 'Commission Ready' },
    { value: 'document_uploaded', label: 'Document Uploaded' },
    { value: 'system_alert', label: 'System Alert' },
  ];

  useEffect(() => {
    fetchNotifications();
  }, [activeTab, page, typeFilter]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        per_page: perPage,
      };

      if (activeTab === 1) {
        params.is_read = false;
      }

      if (typeFilter) {
        params.type = typeFilter;
      }

      const response = await api.get('/in-app-notifications', { params });
      setNotifications(response.data.notifications);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
      setSelectedIds([]);
    } catch (err) {
      setError('Failed to load notifications');
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSelectAll = () => {
    if (selectedIds.length === notifications.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(notifications.map((n) => n.id));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleMarkSelectedAsRead = async () => {
    try {
      await Promise.all(
        selectedIds.map((id) => api.put(`/in-app-notifications/${id}/read`))
      );
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (err) {
      setError('Failed to mark notifications as read');
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} notification(s)?`)) {
      return;
    }

    try {
      await Promise.all(
        selectedIds.map((id) => api.delete(`/in-app-notifications/${id}`))
      );
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (err) {
      setError('Failed to delete notifications');
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      try {
        await api.put(`/in-app-notifications/${notification.id}/read`);
        await fetchNotifications();
        await fetchUnreadCount();
      } catch (err) {
        console.error('Error marking as read:', err);
      }
    }

    // Navigate if link exists
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleDeleteOne = async (id) => {
    if (!window.confirm('Delete this notification?')) {
      return;
    }

    try {
      await api.delete(`/in-app-notifications/${id}`);
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (err) {
      setError('Failed to delete notification');
    }
  };

  const handleFilterOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (type) => {
    setTypeFilter(type);
    setPage(1);
    handleFilterClose();
  };

  const getNotificationIcon = (type) => {
    const iconProps = { fontSize: 'small' };

    switch (type) {
      case 'lead_new':
      case 'lead_status':
        return <PersonAdd {...iconProps} />;
      case 'sale_scheduled':
      case 'sale_completed':
        return <ShoppingCart {...iconProps} />;
      case 'appointment_reminder':
        return <Event {...iconProps} />;
      case 'inventory_low':
        return <Inventory {...iconProps} />;
      case 'payment_received':
        return <Payment {...iconProps} />;
      case 'commission_ready':
        return <AttachMoney {...iconProps} />;
      case 'document_uploaded':
        return <Description {...iconProps} />;
      case 'system_alert':
        return <Warning {...iconProps} />;
      default:
        return <Info {...iconProps} />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'lead_new':
        return 'primary';
      case 'lead_status':
        return 'info';
      case 'sale_scheduled':
      case 'sale_completed':
        return 'success';
      case 'appointment_reminder':
        return 'warning';
      case 'payment_received':
      case 'commission_ready':
        return 'success';
      case 'system_alert':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatNotificationTime = (createdAt) => {
    try {
      return formatDistanceToNow(new Date(createdAt), { addSuffix: true });
    } catch (error) {
      return createdAt;
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Notifications
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={`All (${total})`} />
            <Tab label="Unread" />
          </Tabs>
          <Box sx={{ pr: 2 }}>
            <Button
              startIcon={<FilterList />}
              onClick={handleFilterOpen}
              size="small"
            >
              {typeFilter ? notificationTypes.find(t => t.value === typeFilter)?.label : 'Filter by Type'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {selectedIds.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'action.hover', display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2">
              {selectedIds.length} selected
            </Typography>
            <Button
              size="small"
              onClick={handleMarkSelectedAsRead}
              disabled={loading}
            >
              Mark as Read
            </Button>
            <Button
              size="small"
              color="error"
              onClick={handleDeleteSelected}
              disabled={loading}
            >
              Delete
            </Button>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {activeTab === 1 ? 'No unread notifications' : 'No notifications'}
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ p: 0 }}>
              <ListItem
                sx={{
                  bgcolor: 'action.hover',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Checkbox
                  checked={selectedIds.length === notifications.length}
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < notifications.length
                  }
                  onChange={handleSelectAll}
                />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Select All
                </Typography>
              </ListItem>

              {notifications.map((notification) => (
                <ListItem
                  key={notification.id}
                  sx={{
                    backgroundColor: notification.is_read
                      ? 'transparent'
                      : 'action.hover',
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:hover': {
                      backgroundColor: 'action.selected',
                    },
                  }}
                >
                  <Checkbox
                    checked={selectedIds.includes(notification.id)}
                    onChange={() => handleSelectOne(notification.id)}
                  />
                  <ListItemIcon
                    onClick={() => handleNotificationClick(notification)}
                    sx={{ cursor: notification.link ? 'pointer' : 'default', ml: 1 }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${getNotificationColor(notification.type)}.light`,
                        color: `${getNotificationColor(notification.type)}.main`,
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    onClick={() => handleNotificationClick(notification)}
                    sx={{ cursor: notification.link ? 'pointer' : 'default' }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: notification.is_read ? 'normal' : 'bold',
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.is_read && (
                          <Chip label="New" size="small" color="primary" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Stack spacing={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatNotificationTime(notification.created_at)}
                        </Typography>
                      </Stack>
                    }
                  />
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteOne(notification.id)}
                  >
                    <Delete />
                  </IconButton>
                </ListItem>
              ))}
            </List>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
      >
        <MenuItem onClick={() => handleFilterSelect(null)}>
          <Typography variant="body2">All Types</Typography>
        </MenuItem>
        <Divider />
        {notificationTypes.map((type) => (
          <MenuItem
            key={type.value}
            onClick={() => handleFilterSelect(type.value)}
            selected={typeFilter === type.value}
          >
            {type.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default Notifications;
